import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Quote as QuoteIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/testimonials`)
      .then((r) => alive && setItems(r.data || []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const total = items.length;
  const current = items[idx];

  useEffect(() => {
    if (total < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6800);
    return () => clearInterval(t);
  }, [total]);

  const go = (delta) => {
    if (!total) return;
    setIdx((i) => (i + delta + total) % total);
  };

  return (
    <section id="testimonials" className="relative bg-[#222222] py-24 lg:py-32 overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 lg:mb-20">
          <Reveal>
            <div className="section-label mb-6">Client Voice · Testimonials</div>
            <h2 className="font-display text-[#FAFAFA] text-[40px] sm:text-[56px] lg:text-[76px] leading-[0.95] tracking-[-0.03em] max-w-[16ch]">
              What our<br />clients <span className="text-[#A4161A]">say</span>.
            </h2>
          </Reveal>
          {total > 1 && (
            <Reveal delay={0.15}>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => go(-1)}
                  className="w-12 h-12 rounded-full border border-[#FAFAFA]/25 text-[#FAFAFA] flex items-center justify-center hover:bg-[#A4161A] hover:border-[#A4161A] transition-colors duration-500"
                  aria-label="Previous testimonial"
                  data-testid="testimonial-prev"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="text-[#9A9A9A] font-btn text-[11px] tracking-[0.3em] uppercase" data-testid="testimonial-counter">
                  {String(idx + 1).padStart(2, "0")} · {String(total).padStart(2, "0")}
                </div>
                <button
                  onClick={() => go(1)}
                  className="w-12 h-12 rounded-full border border-[#FAFAFA]/25 text-[#FAFAFA] flex items-center justify-center hover:bg-[#A4161A] hover:border-[#A4161A] transition-colors duration-500"
                  aria-label="Next testimonial"
                  data-testid="testimonial-next"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </Reveal>
          )}
        </div>

        <div className="relative min-h-[280px] lg:min-h-[320px]">
          <AnimatePresence mode="wait">
            {current && (
              <motion.blockquote
                key={current.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
                data-testid="testimonial-card"
              >
                <QuoteIcon size={44} className="text-[#A4161A] mb-6" strokeWidth={1.4} />
                <p className="font-display text-[#FAFAFA] text-[24px] sm:text-[30px] lg:text-[40px] leading-[1.2] tracking-[-0.02em] max-w-[36ch]">
                  “{current.quote}”
                </p>
                <footer className="mt-10 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#A4161A]/15 border border-[#A4161A]/40 text-[#A4161A] font-display text-[16px] flex items-center justify-center">
                    {current.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <div className="text-[#FAFAFA] font-btn text-[14px] tracking-tight">{current.name}</div>
                    <div className="text-[#9A9A9A] text-[12px] tracking-[0.15em] uppercase mt-1">
                      {current.role} · {current.company}
                    </div>
                  </div>
                </footer>
              </motion.blockquote>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
